/**
 * RevolutionSelectbox — custom dropdown/selectbox widget.
 * <localization-selectors> — custom element wrapping market/language selectors.
 *
 * Extracted from global.js. Loaded as ES module via importmap alias
 * `@theme/selectbox`. Exposes `window.RevolutionSelectbox` so classic
 * consumers in `global.js` (ProductElement etc.) can still use it.
 *
 * Dependencies:
 *   - @theme/delegate, @theme/responsive, @theme/body-scroll-lock-options
 *   - @theme/focus-trap
 *   - @theme/body-scroll-lock
 */

import { Delegate } from '@theme/delegate';
import { responsive } from '@theme/responsive';
import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';
import { trapFocus, removeTrapFocus } from '@theme/focus-trap';
import { disableBodyScroll, clearAllBodyScrollLocks } from '@theme/body-scroll-lock';

export class RevolutionSelectbox {
  constructor(element, popover, additionalOverlay) {
    this.element = element;
    this.popover = popover;
    this.changeOverlay = additionalOverlay;
    this.domDelegate = new Delegate(this.element);
    this.delegateBody = new Delegate(document.body);
    this.dropdown = this.element.querySelector(".RevolutionSelectbox");
    this.isOpen = false;
    this.mainDropdown = this.element.querySelector(".RevolutionSelectbox--Content");
    this.mainDropdownContent = this.element.querySelector(".RevolutionSelectbox--InnerContent");
    this.mainButton = this.element.querySelector(".RevolutionSelectbox--Button");

    this.connectedSelect = this.element.getAttribute("data-select");
    this.selectbox = this.element.querySelector("[name=" + this.connectedSelect + "]");
    if (this.selectbox == null) {
      this.selectbox = this.element.querySelector("[name2=" + this.connectedSelect + "]");
    }

    if (this.popover == true) {
      this.mainDropdown.classList.remove("RevolutionSelectbox--animation");
      this.mainDropdown.classList.add("PopoverContent");
      if (this.changeOverlay) {
        this.pageOverlayElement = document.querySelector(".localizationOverlay");
      } else {
        this.pageOverlayElement = document.querySelector(".pageOverlay");
      }
    }

    this.bindEvents();
    this.initSelectbox();
  }

  bindEvents() {
    this.onClickOutsideListener = this.onClickOutside.bind(this);
    this.domDelegate.on("click", "[data-action=toggle-selectbox]", this.toggleDropdown.bind(this));
    this.domDelegate.on("click", "[data-action=selectbox-submit]", this.selectboxSubmit.bind(this));
    this.domDelegate.on("keypress", "[data-action=selectbox-submit]", this.selectboxSubmit.bind(this));
    this.domDelegate.on("click", "[data-action=selectbox-change]", this.selectboxChange.bind(this));

    this.delegateBody.on("menu:open", this.onClickOutside.bind(this));
    this.domDelegate.on("click", "body", this.onClickOutside.bind(this));
  }

  selectboxChange() {
    var value = event.target.getAttribute("data-value");
    var selectboxID = event.target.getAttribute("data-selectboxid");
    var innerContent = event.target.innerHTML;
    var selEl = this.element.querySelector("#" + selectboxID);
    if (selEl.nodeName == "SELECT") {
      selEl.value = value;
      selEl.dispatchEvent(new Event("change"));
    } else {
      selEl.querySelector('[value="' + value + '"]').setAttribute("checked", "true");
    }
    this.mainButton.querySelector(".RevolutionSelectbox--Title").innerHTML = innerContent;

    if (this.element.classList.contains('dropdownSelector')) {
      this.element.querySelector('.RevolutionSelectbox--Item[disabled="true"]').removeAttribute('disabled');
      event.target.setAttribute('disabled', 'true');
    }
    this.closeDropdown(this.element);
  }

  onClickOutside(event) {
    const withinBoundaries = event.composedPath().includes(this.element);
    if (!withinBoundaries || event.target.classList.contains("FiltersDesktopWrapper")) {
      this.closeDropdown(this.element);
    }
  }

  selectboxSubmit(event) {
    if (event.key != "Enter" && event.key !== undefined) {
      return;
    }
    var value = event.target.getAttribute("data-value");
    var dataName = event.target.getAttribute("data-name");
    this.selectbox.value = value;
    this.closeDropdown(this.element);
    this.mainButton.querySelector(".RevolutionSelectbox--Title").innerHTML = dataName;
    this.selectbox.parentElement.submit();
  }

  initSelectbox() {
    const selectedItem = this.mainDropdown.querySelector('[selected="selected"]');
    const titleElement = this.element.querySelector(".RevolutionSelectbox--Title");
    if (selectedItem) {
      titleElement.innerHTML = selectedItem.getAttribute("data-name");
    }
    trapFocus(this.element);
    this.element.style.display = "block";
  }

  toggleDropdown(event, target) {
    event.preventDefault();
    if (this.isOpen) {
      this.closeDropdown(target);
    } else {
      this.openDropdown(target);
    }
  }

  openDropdown(target) {
    this.element.dispatchEvent(new CustomEvent('close:search', { bubbles: true }));
    this.element.dispatchEvent(new CustomEvent('menu:close', { bubbles: true }));

    const siblings = Array.from(target.parentElement.parentElement.children)
      .filter((item) => item.classList.contains('RevolutionSelectbox'));

    siblings.forEach((item) => {
      item.querySelector('.RevolutionSelectbox--Button').setAttribute('aria-expanded', false);
      item.querySelector('.RevolutionSelectbox--Content').setAttribute('aria-hidden', true);
      item.querySelector('.RevolutionSelectbox--Content').style.display = 'none';
    });

    if (this.popover) {
      this.pageOverlayElement.classList.add('is-visible');
      this.pageOverlayElement.addEventListener('click', this.onClickOutsideListener);
      const focus = this.element.querySelector('[data-scrollable]');
      if (typeof bodyScrollLock !== 'undefined') {
        disableBodyScroll(focus, bodyScrollLockOptions);
      }
      document.body.classList.add('scrollLock');
      trapFocus(this.mainDropdown);
      const drawer = this.element.closest('drawer-component.mobile-menu');
      if (drawer) {
        drawer.querySelector('.Drawer--Wrapper').style.overflow = 'visible';
      }
    }

    const contentElement = target.parentElement.querySelector('.RevolutionSelectbox--Content');
    target.setAttribute('aria-expanded', 'true');
    contentElement.style.display = 'block';
    contentElement.style.height = `${contentElement.offsetHeight}px`;

    if (this.popover) {
      setTimeout(() => {
        contentElement.setAttribute('aria-hidden', 'false');
      }, 20);
    } else {
      contentElement.setAttribute('aria-hidden', 'false');
    }

    this.isOpen = true;
    window.addEventListener('click', this.onClickOutsideListener);
  }

  closeDropdown(target) {
    let closestDropdown = target.closest(".RevolutionSelectbox");
    if (closestDropdown === null) {
      closestDropdown = target.querySelector("[aria-expanded='true']").parentElement;
    }
    closestDropdown.querySelector(".RevolutionSelectbox--Button").setAttribute("aria-expanded", false);
    closestDropdown.querySelector(".RevolutionSelectbox--Content").setAttribute("aria-hidden", true);
    setTimeout(() => {
      closestDropdown.querySelector(".RevolutionSelectbox--Content").style.display = "none";
    }, 300);

    if (this.popover == true) {
      this.pageOverlayElement.classList.remove("is-visible");
      this.pageOverlayElement.removeEventListener("click", this.onClickOutsideListener);

      const menuDrawer = this.element.closest('drawer-component.mobile-menu');
      if (menuDrawer) {
        menuDrawer.querySelector('.Drawer--Wrapper').style.overflow = "auto";
      } else if (typeof bodyScrollLock !== 'undefined') {
        clearAllBodyScrollLocks();
        document.body.classList.remove("scrollLock");
      }
    }

    this.isOpen = false;
    removeTrapFocus(this.mainDropdownContent);
    window.removeEventListener("click", this.onClickOutsideListener);
  }

  destroy() {
    if (this.isOpen) {
      this.closeDropdown(this.element);
    }

    window.removeEventListener("click", this.onClickOutsideListener);
    this.pageOverlayElement?.removeEventListener("click", this.onClickOutsideListener);
    this.domDelegate?.destroy();
    this.delegateBody?.destroy();
    this.domDelegate = null;
    this.delegateBody = null;
    this.element = null;
  }
}

// Bridge for classic-script consumers (global.js ProductElement). Removed in Faza 5.
window.RevolutionSelectbox = RevolutionSelectbox;

class LocalizationSelectors extends HTMLElement {
  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;

    this._modalCountry = document.getElementById('modal-country');
    if (!this._modalCountry) return;
    LocalizationSelectors._instanceCount += 1;
    LocalizationSelectors._modalCountry = this._modalCountry;
    LocalizationSelectors._setupModalObserver();
    LocalizationSelectors._syncOutsideClickBinding();
  }

  disconnectedCallback() {
    if (!this._mounted) return;
    this._mounted = false;

    if (LocalizationSelectors._instanceCount > 0) {
      LocalizationSelectors._instanceCount -= 1;
    }

    if (LocalizationSelectors._instanceCount === 0) {
      LocalizationSelectors._teardownModalObserver();
      LocalizationSelectors._unbindOutsideClick();
      LocalizationSelectors._modalCountry = null;
    }

    this._modalCountry = null;
  }

  static _getModalCountry() {
    if (this._modalCountry && document.contains(this._modalCountry)) return this._modalCountry;
    this._modalCountry = document.getElementById('modal-country');
    return this._modalCountry;
  }

  static _setupModalObserver() {
    const modal = this._getModalCountry();
    if (!modal || this._modalObserver) return;

    this._modalObserver = new MutationObserver(() => {
      LocalizationSelectors._syncOutsideClickBinding();
    });
    this._modalObserver.observe(modal, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  }

  static _teardownModalObserver() {
    if (!this._modalObserver) return;
    this._modalObserver.disconnect();
    this._modalObserver = null;
  }

  static _getOutsideClickHandler() {
    if (this._outsideClickHandler) return this._outsideClickHandler;

    this._outsideClickHandler = (event) => {
      const modal = LocalizationSelectors._getModalCountry();
      if (!modal) return;
      if (modal.getAttribute('aria-hidden') !== 'false') return;

      const toggleBtn = event.target.closest('[data-action="toggle-popup"][aria-controls="modal-country"]');
      if (toggleBtn) return;

      if (!modal.contains(event.target)) {
        if (window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
          window.globalPopup.closePopup(modal);
        } else {
          modal.setAttribute('aria-hidden', 'true');
        }
      }
    };

    return this._outsideClickHandler;
  }

  static _syncOutsideClickBinding() {
    const modal = this._getModalCountry();
    if (!modal) {
      this._unbindOutsideClick();
      return;
    }

    const isOpen = modal.getAttribute('aria-hidden') === 'false';
    if (isOpen) {
      if (this._isOutsideClickBound) return;
      document.addEventListener('mousedown', this._getOutsideClickHandler(), true);
      this._isOutsideClickBound = true;
      return;
    }

    this._unbindOutsideClick();
  }

  static _unbindOutsideClick() {
    if (!this._isOutsideClickBound) return;
    document.removeEventListener('mousedown', this._getOutsideClickHandler(), true);
    this._isOutsideClickBound = false;
  }
}
if (!customElements.get('localization-selectors')) {
  customElements.define('localization-selectors', LocalizationSelectors);
}

LocalizationSelectors._instanceCount = 0;
LocalizationSelectors._isOutsideClickBound = false;
LocalizationSelectors._outsideClickHandler = null;
LocalizationSelectors._modalObserver = null;
LocalizationSelectors._modalCountry = null;

class LocalizationModal extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.enableDropdownAnimations = window.theme
      ? window.theme.enableDropdownAnimations !== false
      : true;
  }

  /**
   * Mirrors Theme settings → Localization → Hide localization header close.
   * Ensures the mobile header “CLOSE” control is removed even if Liquid output is stale.
   */
  #applyLocalizationHeaderClosePreference() {
    if (window.theme?.localizationHideCountrySelectorClose !== true) return;
    const modal = document.getElementById('modal-country');
    modal?.classList.add('localization--hide-country-selector-close');
    this.element.querySelector('button.Drawer--Close.country_selector__close')?.remove();
  }

  connectedCallback() {
    this.#applyLocalizationHeaderClosePreference();
    this.delegateElement = new Delegate(this.element);
    this.element
      .querySelectorAll('.RevolutionSelectbox')
      .forEach((box) => new RevolutionSelectbox(box));
    this.bindEvents();
    this.ensureLocalizationData();
    if (this.ensureLanguageSelection) this.ensureLanguageSelection();
    this.sortMarketItems();
    this.enableTouchScrollGuard();
    this.observeSelectboxExpandedState();
    this.observeModalClose();

    this.element.querySelectorAll('.RevolutionSelectbox--Selectors').forEach((box) => {
      const btn = box.querySelector('.RevolutionSelectbox--Button');
      const panel = box.querySelector('.RevolutionSelectbox--Content');
      const count = box.querySelectorAll('.RevolutionSelectbox--Item').length;
      if (!btn || !panel) return;
      if (count <= 1) {
        btn.classList.add('rs-disabled');
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');
        panel.style.display = 'none';
      } else {
        btn.classList.remove('rs-disabled');
        btn.removeAttribute('disabled');
        btn.setAttribute('aria-disabled', 'false');
      }
    });
  }

  bindEvents() {
    this._onDropdownOpened = this.checkFadeVisibility.bind(this);
    document.addEventListener('dropdown:opened', this._onDropdownOpened);
    document.body.addEventListener('selectbox:opened', this._onDropdownOpened);

    this._onCountryChange = this.updateLanguageOptions.bind(this);
    document.addEventListener('change:countrySelector', this._onCountryChange);

    this._onResize = this.checkFadeVisibility.bind(this);
    window.addEventListener('resize', this._onResize);

    this._onLocalizationSelectboxClick = this.handleLocalizationSelectboxClick.bind(this);
    this.element.addEventListener('click', this._onLocalizationSelectboxClick, true);

    this.delegateElement.on(
      'click',
      '[data-action=check-location-submit]',
      this.checkLocationSubmit.bind(this)
    );
    this.delegateElement.on('click', '.RevolutionSelectbox--Item', (e, target) => {
      const box = target.closest('.RevolutionSelectbox');
      if (!box) return;
      if (target.closest('.rs-country__selectbox')) {
        this.handleRegularMarketSelection(e, target);
      } else {
        this.syncSelectboxUI(box, target);
        requestAnimationFrame(() => {
          this.closeSelectbox(box);
          // Switch deferred to the "Confirm" submit button.
        });
      }
    });
    this.delegateElement.on('keydown', '.RevolutionSelectbox--Item', (e, target) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      target.click();
    });
    this.delegateElement.on('click', '.RevolutionSelectbox--Button', () => {
      // Wait for selectbox open/close DOM updates, then reuse existing fade-visible logic.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.checkFadeVisibility());
      });
    });
    this._panelScrollHandlers = [];
    this.element.querySelectorAll('.RevolutionSelectbox--Content').forEach((panel) => {
      const scrollEl = this._getScrollEl(panel);
      const fn = this.checkFadeVisibility.bind(this);
      this._panelScrollHandlers.push({ panel: scrollEl, fn });
      scrollEl.addEventListener('scroll', fn);
    });
  }

  disconnectedCallback() {
    if (this._onDropdownOpened) {
      document.removeEventListener('dropdown:opened', this._onDropdownOpened);
      document.body.removeEventListener('selectbox:opened', this._onDropdownOpened);
    }
    if (this._onCountryChange) document.removeEventListener('change:countrySelector', this._onCountryChange);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._onLocalizationSelectboxClick) {
      this.element.removeEventListener('click', this._onLocalizationSelectboxClick, true);
    }
    if (this._panelScrollHandlers) {
      this._panelScrollHandlers.forEach(({ panel, fn }) => panel.removeEventListener('scroll', fn));
    }
    if (this._touchScrollHandlers) {
      this._touchScrollHandlers.forEach(({ el, startFn, moveFn }) => {
        el.removeEventListener('touchstart', startFn);
        el.removeEventListener('touchmove', moveFn);
      });
    }
    this._onDropdownOpened = null;
    this._onCountryChange = null;
    this._onResize = null;
    this._onLocalizationSelectboxClick = null;
    this._panelScrollHandlers = null;
    this._touchScrollHandlers = null;
    if (this._selectboxExpandedObserver) {
      this._selectboxExpandedObserver.disconnect();
      this._selectboxExpandedObserver = null;
    }
    if (this._modalCloseObserver) {
      this._modalCloseObserver.disconnect();
      this._modalCloseObserver = null;
    }
  }

  observeSelectboxExpandedState() {
    this._selectboxExpandedObserver?.disconnect?.();
    this._selectboxExpandedObserver = new MutationObserver(() => {
      this.syncLocalizationPopupExpanded();
    });

    this.element.querySelectorAll('.RevolutionSelectbox--Button').forEach((button) => {
      this._selectboxExpandedObserver.observe(button, {
        attributes: true,
        attributeFilter: ['aria-expanded'],
      });
    });

    this.syncLocalizationPopupExpanded();
  }

  // When the localization popup closes, collapse any open market/language
  // dropdown so it doesn't reappear open the next time the popup opens.
  observeModalClose() {
    const modal = this.closest('#modal-country') || document.getElementById('modal-country');
    if (!modal) return;
    this._modalCloseObserver?.disconnect?.();
    this._modalCloseObserver = new MutationObserver(() => {
      if (modal.getAttribute('aria-hidden') === 'true') this.resetSelectboxes();
    });
    this._modalCloseObserver.observe(modal, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  }

  resetSelectboxes() {
    this.element.querySelectorAll('.RevolutionSelectbox--Selectors').forEach((box) => {
      const btn = box.querySelector('.RevolutionSelectbox--Button');
      const panel = box.querySelector('.RevolutionSelectbox--Content');
      if (!btn || !panel) return;
      btn.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      panel.style.maxHeight = '0px';
    });
    this.syncLocalizationPopupExpanded();
  }

  enableTouchScrollGuard() {
    this._touchScrollHandlers = [];
    this.element
      .querySelectorAll('.RevolutionSelectbox--Selectors .RevolutionSelectbox--Content-scroll')
      .forEach((el) => {
        let startY = 0;

        const startFn = (event) => {
          startY = event.touches?.[0]?.clientY || 0;
        };

        const moveFn = (event) => {
          const currentY = event.touches?.[0]?.clientY || 0;
          const deltaY = startY - currentY;
          const atTop = el.scrollTop <= 0;
          const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

          // Keep touch scrolling inside dropdown on iOS instead of bubbling to popup/page.
          if ((atTop && deltaY < 0) || (atBottom && deltaY > 0)) {
            event.preventDefault();
          }
          event.stopPropagation();
        };

        el.addEventListener('touchstart', startFn, { passive: true });
        el.addEventListener('touchmove', moveFn, { passive: false });
        this._touchScrollHandlers.push({ el, startFn, moveFn });
      });
  }


  sortMarketItems() {
    const inner = this.element.querySelector(
      '.rs-country__selectbox .RevolutionSelectbox--InnerContent'
    );
    if (!inner) return;

    const items = Array.from(inner.querySelectorAll('.RevolutionSelectbox--Item'));
    if (!items.length) return;

    const isGlobal = (el) =>
      (el.getAttribute('data-name') || '').trim().toLowerCase() === 'global';

    const globalItems = items.filter(isGlobal);
    const rest = items.filter((el) => !isGlobal(el));

    rest.sort((a, b) => {
      const an = (a.getAttribute('data-name') || '').trim();
      const bn = (b.getAttribute('data-name') || '').trim();
      return an.localeCompare(bn, undefined, { sensitivity: 'base' });
    });

    // Rebuild DOM in order
    inner.textContent = '';
    [...globalItems, ...rest].forEach((el) => inner.appendChild(el));
  }


  _getScrollEl(panel) {
    const innerScroll = panel.querySelector('.RevolutionSelectbox--Content-scroll');
    if (!innerScroll) return panel;

    const innerScrollable = innerScroll.scrollHeight > innerScroll.clientHeight + 1;
    if (innerScrollable) return innerScroll;

    return panel;
  }

  handleLocalizationSelectboxClick(event) {
    const button = event.target.closest('.RevolutionSelectbox--Button[data-action="toggle-selectbox"]');
    if (button && this.element.contains(button)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (
        button.classList.contains('rs-disabled') ||
        button.disabled ||
        button.getAttribute('aria-disabled') === 'true'
      ) {
        this.closeLocalizationSelectbox(button.closest('.RevolutionSelectbox'));
        return;
      }

      this.toggleLocalizationSelectbox(button.closest('.RevolutionSelectbox'));
      return;
    }

    const item = event.target.closest('.RevolutionSelectbox--Item[data-action="selectbox-change"]');
    if (!item || !this.element.contains(item)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const box = item.closest('.RevolutionSelectbox');
    if (!box) return;

    if (item.closest('.rs-country__selectbox')) {
      this.handleRegularMarketSelection(event, item);
      return;
    }

    this.syncSelectboxUI(box, item);
    this.closeSelectbox(box);
    // Switch deferred to the "Confirm" submit button — see handleRegularMarketSelection.
  }

  toggleLocalizationSelectbox(box) {
    if (!box) return;

    const button = box.querySelector('.RevolutionSelectbox--Button');
    const isOpen = button?.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      this.closeLocalizationSelectbox(box);
    } else {
      this.openLocalizationSelectbox(box);
    }
  }

  openLocalizationSelectbox(box) {
    if (!box) return;

    this.setLocalizationPopupExpanded(true);

    this.element.querySelectorAll('.RevolutionSelectbox--Selectors').forEach((otherBox) => {
      if (otherBox !== box) this.closeLocalizationSelectbox(otherBox);
    });

    const button = box.querySelector('.RevolutionSelectbox--Button');
    const panel = box.querySelector('.RevolutionSelectbox--Content');
    if (!button || !panel) return;

    const token = (panel._localizationSelectboxToken || 0) + 1;
    panel._localizationSelectboxToken = token;
    button.setAttribute('aria-expanded', 'true');
    panel.style.display = 'block';
    panel.style.height = '';
    panel.setAttribute('aria-hidden', 'false');

    if (!this.enableDropdownAnimations) {
      panel.style.maxHeight = `${this.getLocalizationPanelHeight(panel)}px`;
      this.checkFadeVisibility();
      return;
    }

    panel.style.maxHeight = '0px';
    requestAnimationFrame(() => {
      if (panel._localizationSelectboxToken !== token) return;
      const maxHeight = this.getLocalizationPanelHeight(panel);
      panel.style.maxHeight = `${maxHeight}px`;
      this.checkFadeVisibility();
    });
  }

  closeLocalizationSelectbox(box) {
    if (!box) return;

    const button = box.querySelector('.RevolutionSelectbox--Button');
    const panel = box.querySelector('.RevolutionSelectbox--Content');
    if (!button || !panel) return;

    const token = (panel._localizationSelectboxToken || 0) + 1;
    panel._localizationSelectboxToken = token;
    button.setAttribute('aria-expanded', 'false');
    panel.style.display = 'block';
    panel.style.height = '';

    if (!this.enableDropdownAnimations) {
      panel.setAttribute('aria-hidden', 'true');
      panel.style.maxHeight = '0px';
      this.syncLocalizationPopupExpanded();
      this.checkFadeVisibility();
      return;
    }

    panel.style.maxHeight = `${panel.offsetHeight || 0}px`;
    requestAnimationFrame(() => {
      if (panel._localizationSelectboxToken !== token) return;
      panel.setAttribute('aria-hidden', 'true');
      panel.style.maxHeight = '0px';
      this.syncLocalizationPopupExpanded();
      this.checkFadeVisibility();
    });
  }

  setLocalizationPopupExpanded(isExpanded) {
    const modal = this.closest('#modal-country');
    if (!modal) return;

    modal.classList.toggle('country-selector--expanded', isExpanded);
    this.positionLocalizationPopup(modal, isExpanded);
    if (isExpanded) {
      modal.scrollTop = 0;
      const body = modal.querySelector('.country-selector__body');
      if (body) body.scrollTop = 0;
    }
  }

  positionLocalizationPopup(modal, isExpanded) {
    if (!modal) return;

    if (!isExpanded) {
      modal.style.removeProperty('top');
      modal.style.removeProperty('max-height');
      return;
    }

    const top = this.getLocalizationExpandedTop();
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const bottomSpace = 8;

    modal.style.top = `${top}px`;
    modal.style.maxHeight = `${Math.max(260, viewportHeight - top - bottomSpace)}px`;
  }

  /**
   * Parses `#modal-country` `--localization-popup-offset-from-chrome` (e.g. 3.125rem) for JS layout.
   * @param {HTMLElement} modal
   * @returns {number}
   */
  #getLocalizationPopupChromeOffsetPx(modal) {
    const rootFont = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
    const raw = window.getComputedStyle(modal).getPropertyValue('--localization-popup-offset-from-chrome').trim();
    if (raw.endsWith('rem')) {
      return Math.round((parseFloat(raw) || 3.125) * rootFont);
    }
    if (raw.endsWith('px')) {
      const n = parseFloat(raw);
      return Number.isFinite(n) ? Math.round(n) : Math.round(3.125 * rootFont);
    }
    return Math.round(3.125 * rootFont);
  }

  getLocalizationExpandedTop() {
    const modal = document.getElementById('modal-country');
    const offsetPx = modal ? this.#getLocalizationPopupChromeOffsetPx(modal) : Math.round(3.125 * 16);
    const rootStyles = window.getComputedStyle(document.documentElement);
    const parsePx = (value) => {
      const n = parseFloat(String(value || '').replace('px', '').trim());
      return Number.isFinite(n) ? n : 0;
    };

    return Math.max(
      0,
      Math.round(parsePx(rootStyles.getPropertyValue('--announcement-dynamic-height')) + offsetPx)
    );
  }

  syncLocalizationPopupExpanded() {
    const hasOpenSelectbox = Array.from(
      this.element.querySelectorAll('.RevolutionSelectbox--Button')
    ).some((button) => button.getAttribute('aria-expanded') === 'true');

    this.setLocalizationPopupExpanded(hasOpenSelectbox);
  }

  getLocalizationPanelHeight(panel) {
    const capPx = this.getLocalizationPanelMaxHeight(panel);
    if (panel.closest('localization-modal')) {
      return capPx;
    }
    const scrollEl = panel.querySelector('.RevolutionSelectbox--Content-scroll');
    const contentHeight = scrollEl ? scrollEl.scrollHeight : panel.scrollHeight;
    const cssMaxHeight = parseFloat(window.getComputedStyle(panel).maxHeight);
    const maxHeight = Number.isFinite(cssMaxHeight) && cssMaxHeight > 0 ? cssMaxHeight : capPx;
    return Math.min(contentHeight, maxHeight);
  }

  getLocalizationPanelMaxHeight(panel) {
    const modal = panel.closest('#modal-country');
    const raw = window.getComputedStyle(modal || panel)
      .getPropertyValue('--localization-selector-panel-max-height')
      .trim();

    if (raw.endsWith('rem')) {
      const rootFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
      return (parseFloat(raw) || 13.75) * rootFontSize;
    }

    if (raw.endsWith('px')) return parseFloat(raw) || 213;

    return 213;
  }

  closeSelectbox(box) {
    if (!box) return;

    if (this.element.contains(box)) {
      this.closeLocalizationSelectbox(box);
      return;
    }

    const btn = box.querySelector('.RevolutionSelectbox--Button');
    const panel = box.querySelector('.RevolutionSelectbox--Content');
    if (btn && panel) {
      btn.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      panel.style.display = 'none';
    }
  }

  checkFadeVisibility() {
    const panels = Array.from(this.element.querySelectorAll('.RevolutionSelectbox--Content'));
    panels.forEach((panel) => {
      const isOpen =
        panel.getAttribute('aria-hidden') === 'false' &&
        getComputedStyle(panel).display !== 'none';
      if (!isOpen) {
        panel.classList.remove('fade-visible');
        return;
      }
      const scrollEl = this._getScrollEl(panel);
      const isScrollable = scrollEl.scrollHeight > scrollEl.clientHeight + 1;
      const distanceFromBottom =
        scrollEl.scrollHeight - (scrollEl.scrollTop + scrollEl.clientHeight);
      const isAtBottom = distanceFromBottom <= 2;
      panel.classList.toggle('fade-visible', isScrollable && !isAtBottom);
    });
  }

  escapeHtml(value) {
    const span = document.createElement('span');
    span.textContent = value || '';
    return span.innerHTML;
  }

  getMarketName(item) {
    const marketName = (item.getAttribute('data-market-name') || '').trim();
    if (marketName) return marketName;

    const name = (item.getAttribute('data-name') || '').trim();
    return name.split('/')[0].trim() || name;
  }

  updateMarketDescription(item) {
    const description = this.element.querySelector('#localization-modal-desc');
    const template = this.element.getAttribute('data-description-template');
    if (!description || !template || !item) return;

    const market = this.getMarketName(item);
    const currency = (item.getAttribute('data-currency') || '').trim();
    if (!market || !currency) return;

    description.innerHTML = template
      .split('%%MARKET%%').join(this.escapeHtml(market))
      .split('%%CURRENCY%%').join(this.escapeHtml(currency));
  }


  syncSelectboxUI(box, item) {
    const value = (item.getAttribute('data-value') || '').trim();
    const name = (item.getAttribute('data-name') || '').trim();
    const inputId = (item.getAttribute('data-selectboxid') || '').trim();

    if (inputId && value) {
      const hidden = document.getElementById(inputId);
      if (hidden) hidden.value = value;
    }

    const titleEl = box.querySelector('.RevolutionSelectbox--Button .RevolutionSelectbox--Title');
    if (titleEl && name) titleEl.textContent = name;

    // Keep selected market flag visible in the button label.
    if (box.classList.contains('rs-country__selectbox')) {
      const selectedFlag = item.querySelector('.revolution-selectbox__flag');
      const buttonFlag = box.querySelector('.RevolutionSelectbox--Button .revolution-selectbox__flag');
      if (buttonFlag && selectedFlag) {
        buttonFlag.innerHTML = selectedFlag.innerHTML;
      }
    }

    box.querySelectorAll('.RevolutionSelectbox--Item[aria-selected="true"]').forEach((el) => {
      if (el !== item) el.setAttribute('aria-selected', 'false');
    });
    item.setAttribute('aria-selected', 'true');

  }


  async checkLocationSubmit(event) {
    if (event?.preventDefault) event.preventDefault();
    try {
      const selectedItem =
        this.element.querySelector(
          '.RevolutionSelectbox--Item[aria-selected="true"]'
        ) || this.element.querySelector('.RevolutionSelectbox--Item.selected');
      if (!selectedItem) {
        console.warn('[Localization] No selected market item found.');
        return;
      }

      const chosenCountryCode = (
        selectedItem.getAttribute('data-value') || ''
      ).trim();
      if (!chosenCountryCode) {
        console.warn('[Localization] Selected market has empty data-value.');
        return;
      }

      const redirectUrl = (selectedItem.getAttribute('data-redirect-url') || '').trim();
      const isFramedContext = (() => {
        try {
          return window.self !== window.top;
        } catch (e) {
          return true;
        }
      })();
      // In framed/editor contexts (e.g. theme preview), external redirects can be blocked by CSP.
      // Fallback to normal localization form submit in that case.
      if (redirectUrl && !isFramedContext) {
        window.location.href = redirectUrl;
        return;
      }

      const countryInput = document.querySelector(
        '#country-selector__country-code'
      );
      if (countryInput) countryInput.value = chosenCountryCode;
      if (this.ensureLanguageSelection) this.ensureLanguageSelection();
      const hiddenLang = this.element.querySelector('#selected-language');
      if (hiddenLang) {
        // If no radio selected yet, select first visible one
        if (!hiddenLang.value) {
          const firstRadio = this.element.querySelector(
            '.localization__language-radio'
          );
          if (firstRadio) {
            firstRadio.checked = true;
            hiddenLang.value = firstRadio.value;
          }
        }
      }

      const form = document.getElementById('localization-form');
      if (form) {
        form.submit();
      } else {
        console.error('[Localization] localization-form not found');
      }
    } catch (error) {
      console.error('Localization submit error:', error);
    }
  }

  handleRegularMarketSelection(e, target) {
    if (!target.closest('.rs-country__selectbox')) return;

    this.element
      .querySelectorAll(
        '.rs-country__selectbox .RevolutionSelectbox--Item[aria-selected="true"]'
      )
      .forEach((item) => {
        if (item !== target) item.setAttribute('aria-selected', 'false');
      });
    target.setAttribute('aria-selected', 'true');


    const countryInput = this.element.querySelector(
      '#country-selector__country-code'
    );
    const value = target.getAttribute('data-value');
    if (countryInput && value) {
      countryInput.value = value.trim();
      const languages = target.getAttribute('data-languages') || '';
      document.dispatchEvent(
        new CustomEvent('change:countrySelector', {
          detail: { country: value.trim(), languages }
        })
      );
      const box = target.closest('.RevolutionSelectbox');
      if (box) this.syncSelectboxUI(box, target);

    }

    if (this.ensureLanguageSelection) this.ensureLanguageSelection();
    const box = target.closest('.RevolutionSelectbox');
    if (box) this.closeSelectbox(box);
    // No submit here: selecting a market only updates the hidden country_code
    // input and rebuilds the language list. The actual switch happens when the
    // user clicks "Confirm" (the localization form's submit button).
  }

  ensureLanguageSelection() {
    const hidden = this.element.querySelector('#selected-language');
    const radios = Array.from(
      this.element.querySelectorAll('.language-selector__radio')
    );
    if (!radios.length) return;
    let checked = radios.find((r) => r.checked);
    if (!checked && hidden && hidden.value) {
      checked = radios.find(
        (r) => r.value.toLowerCase() === hidden.value.toLowerCase()
      );
    }
    if (!checked) checked = radios[0];
    if (checked && !checked.checked) checked.checked = true;
    if (hidden && checked && hidden.value !== checked.value)
      hidden.value = checked.value;
  }

  async ensureLocalizationData() {
    if (this._localizationData) return this._localizationData;
    if (!this._localizationDataPromise) this._localizationDataPromise = Promise.resolve(null);
    return this._localizationDataPromise;
  }

  parseLanguages(raw) {
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((l) => ({
          iso: (l.iso || l.code || '').toLowerCase(),
          name: l.name || l.endonym_name || (l.iso || '').toUpperCase(),
        }))
        .filter((l) => l.iso);
    } catch {
      return [];
    }
  }

  async fallbackLanguagesFromJson(countryIso) {
    if (!countryIso) return [];

    const item = Array.from(
      this.element.querySelectorAll('.rs-country__selectbox .RevolutionSelectbox--Item')
    ).find(
      (el) =>
        (el.getAttribute('data-value') || '').toUpperCase() ===
        countryIso.toUpperCase()
    );
    const langs = this.parseLanguages(item?.getAttribute('data-languages') || '');
    if (langs.length) return langs;

    const data = await this.ensureLocalizationData();
    if (!data) return [];

    const match = (data.available_countries || []).find(
      (c) => c.iso_code?.toUpperCase() === countryIso.toUpperCase()
    );
    if (!match) return [];

    return (match.available_languages || [])
      .map((l) => ({
        iso: (l.iso_code || '').toLowerCase(),
        name: (l.endonym_name || l.name || l.iso_code || '').replace(
          /\b\w/g,
          (c) => c.toUpperCase()
        ),
      }))
      .filter((l) => l.iso);
  }

  rebuildLanguageList(langs) {
    const langBox = this.querySelector('.rs-language__selectbox');
    const hidden = this.querySelector('#language-selector__language-code');
    if (!langBox || !hidden || !langs.length) return;

    const inner = langBox.querySelector('.RevolutionSelectbox--InnerContent');
    const btnTitle = langBox.querySelector('.RevolutionSelectbox--Button .RevolutionSelectbox--Title');
    const btn = langBox.querySelector('.RevolutionSelectbox--Button');
    const panel = langBox.querySelector('.RevolutionSelectbox--Content');
    if (!inner || !btn || !panel) return;

    const isSingleLanguage = langs.length === 1;
    const current = (hidden.value || '').toLowerCase();
    const hasCurrent = langs.some((l) => l.iso === current);
    const nextIso = isSingleLanguage ? langs[0].iso : (hasCurrent ? current : langs[0].iso);

    inner.textContent = '';

    langs.forEach((l) => {
      const item = document.createElement('div');
      item.className = 'RevolutionSelectbox--Item';
      item.tabIndex = 0;
      item.setAttribute('data-value', l.iso);
      item.setAttribute('data-name', l.name);
      item.setAttribute('data-selectboxid', 'language-selector__language-code');
      item.setAttribute('data-action', 'selectbox-change');
      item.setAttribute('aria-selected', l.iso === nextIso ? 'true' : 'false');

      const title = document.createElement('div');
      title.className = 'RevolutionSelectbox--Title u-h3';
      title.textContent = l.name;
      item.appendChild(title);

      inner.appendChild(item);
    });

    hidden.value = nextIso;
    const chosen = langs.find((l) => l.iso === nextIso) || langs[0];
    if (btnTitle) btnTitle.textContent = chosen.name;

    if (isSingleLanguage) {
      btn.classList.add('rs-disabled');
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      panel.style.display = 'none';
    } else {
      btn.classList.remove('rs-disabled');
      btn.removeAttribute('disabled');
      btn.setAttribute('aria-disabled', 'false');
    }
  }


  async updateLanguageOptions(e) {
    const raw = e?.detail?.languages || '';
    const countryIso = (e?.detail?.country || '').trim();

    let langs = this.parseLanguages(raw);

    // fallback if data-languages empty/invalid
    if (langs.length === 0) {
      langs = await this.fallbackLanguagesFromJson(countryIso);
    }

    if (langs.length === 0) return;

    this.rebuildLanguageList(langs);
  }
}
customElements.define('localization-modal', LocalizationModal);
